/**
 * POVPresetCard - Radio button card for POV preset selection
 *
 * Displays a selectable card with tags, description, and example text.
 * Uses cinematography metaphor (shot types, intimacy levels).
 */

import type { POVPresetCardProps } from './types'
import { INTIMACY_COLORS, DEFAULT_TAG_COLOR } from './constants'

export function POVPresetCard({ preset, selected, onSelect }: POVPresetCardProps) {
  const { id, label, tags, description, example } = preset

  // Get color for intimacy level tag
  const intimacyColor =
    INTIMACY_COLORS[tags.intimacyLevel as keyof typeof INTIMACY_COLORS] || DEFAULT_TAG_COLOR

  return (
    <label
      data-testid={`pov-preset-card-${id}`}
      className={`
        relative block cursor-pointer rounded-lg border p-4 transition-all
        ${
          selected
            ? 'border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/50'
            : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
        }
      `}
    >
      {/* Hidden radio input for accessibility */}
      <input
        type="radio"
        name="pov-preset"
        value={id}
        checked={selected}
        onChange={() => onSelect(id)}
        className="sr-only"
        aria-label={`Select ${label}`}
      />

      {/* Card content */}
      <div className="space-y-3">
        {/* Title */}
        <h4 className="text-sm font-semibold text-white">{label}</h4>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span
            className={`px-2 py-0.5 text-xs rounded border ${DEFAULT_TAG_COLOR}`}
            title="Shot Type"
          >
            {tags.shotType}
          </span>
          <span
            className={`px-2 py-0.5 text-xs rounded border ${intimacyColor}`}
            title="Intimacy Level"
          >
            {tags.intimacyLevel}
          </span>
          <span
            className={`px-2 py-0.5 text-xs rounded border ${DEFAULT_TAG_COLOR}`}
            title="Interaction Mode"
          >
            {tags.interactionMode}
          </span>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-300 leading-relaxed">{description}</p>

        {/* Example text in monospace box */}
        <div className="mt-2 rounded bg-black/20 px-3 py-2 border border-white/10">
          <p className="text-xs font-mono text-gray-200 italic">{example}</p>
        </div>
      </div>

      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-3 right-3">
          <svg
            className="w-5 h-5 text-blue-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </label>
  )
}
